import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Plus, Send, Paperclip, Image as ImageIcon, PanelLeftClose, PanelLeftOpen,
  Search, MoreHorizontal, Trash2, Pencil, Loader2, Shield, LogOut, MessageSquare,
  X, Sparkles, ChevronDown, FolderOpen, Bookmark, Copy, Settings
} from 'lucide-react';
import { toast } from 'sonner';
import MarkdownRenderer from '../components/MarkdownRenderer';
import * as api from '../lib/api';

export default function ChatPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  
  const [conversations, setConversations] = useState([]);
  const [currentConv, setCurrentConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 1024);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [imageGenMode, setImageGenMode] = useState(false);
  const [editingTitle, setEditingTitle] = useState(null);
  const [editTitleValue, setEditTitleValue] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [projects, setProjects] = useState([]);
  const [showSaveArtifact, setShowSaveArtifact] = useState(false);
  const [saveArtifactData, setSaveArtifactData] = useState({ content: '', title: '', type: 'code', language: '' });
  const [selectedProjectId, setSelectedProjectId] = useState('');
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // Load models and conversations
  useEffect(() => {
    api.getModels().then(setModels).catch(console.error);
    api.listProjects().then(setProjects).catch(console.error);
    loadConversations();
  }, []);

  // Load conversation when ID changes
  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    } else {
      setCurrentConv(null);
      setMessages([]);
    }
  }, [conversationId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const loadConversations = async () => {
    try {
      const convs = await api.listConversations();
      setConversations(convs);
    } catch (e) {
      console.error(e);
    }
  };

  const loadConversation = async (id) => {
    try {
      const conv = await api.getConversation(id);
      setCurrentConv(conv);
      setMessages(conv.messages || []);
      if (conv.model) setSelectedModel(conv.model);
    } catch (e) {
      toast.error('Conversazione non trovata');
      navigate('/chat');
    }
  };

  const handleNewChat = async () => {
    try {
      const conv = await api.createConversation();
      await loadConversations();
      navigate(`/chat/${conv.id}`);
    } catch (e) {
      toast.error('Errore nella creazione della chat');
    }
  };

  const handleSend = async () => {
    if (!inputText.trim() && !selectedFile) return;
    if (isLoading) return;

    let activeConvId = conversationId;
    
    // Create conversation if none exists
    if (!activeConvId) {
      try {
        const conv = await api.createConversation();
        activeConvId = conv.id;
        navigate(`/chat/${conv.id}`, { replace: true });
      } catch (e) {
        toast.error('Errore nella creazione della chat');
        return;
      }
    }

    const text = inputText.trim();
    setInputText('');
    setIsLoading(true);

    // Optimistic user message
    const tempUserMsg = {
      id: 'temp-' + Date.now(),
      role: 'user',
      content: imageGenMode ? `[Genera immagine] ${text}` : text,
      model: imageGenMode ? 'gpt-image-1' : selectedModel,
      attachments: selectedFile ? [{ name: selectedFile.name }] : [],
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      let result;
      if (imageGenMode) {
        result = await api.generateImage(activeConvId, text);
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== tempUserMsg.id);
          return [...filtered, result.user_message, result.assistant_message];
        });
      } else if (selectedFile) {
        // Non-streaming for file uploads
        result = await api.sendMessage(activeConvId, text, selectedModel, selectedFile);
        setMessages(prev => {
          const filtered = prev.filter(m => m.id !== tempUserMsg.id);
          return [...filtered, result.user_message, result.assistant_message];
        });
      } else {
        // Streaming for text-only messages
        let assistantMsgId = null;
        let fullContent = '';
        
        await api.sendMessageStream(
          activeConvId, text, selectedModel,
          // onChunk
          (data) => {
            if (data.type === 'user_msg') {
              setMessages(prev => {
                const filtered = prev.filter(m => m.id !== tempUserMsg.id);
                return [...filtered, data.message];
              });
            } else if (data.type === 'start') {
              assistantMsgId = data.assistant_msg_id;
              setStreamingContent('');
              // Add placeholder assistant message
              setMessages(prev => [...prev, {
                id: data.assistant_msg_id,
                role: 'assistant',
                content: '',
                model: selectedModel,
                _streaming: true,
                created_at: new Date().toISOString()
              }]);
            } else if (data.type === 'chunk') {
              fullContent += data.content;
              setStreamingContent(prev => prev + data.content);
              // Update the streaming message in place
              setMessages(prev => prev.map(m => 
                m.id === assistantMsgId ? { ...m, content: fullContent } : m
              ));
            } else if (data.type === 'title') {
              loadConversations();
            }
          },
          // onDone
          (assistantMessage) => {
            setMessages(prev => prev.map(m => 
              m.id === assistantMsgId ? { ...assistantMessage, _streaming: false } : m
            ));
            setStreamingContent('');
          },
          // onError
          (errorMsg) => {
            toast.error(errorMsg || 'Errore nell\'invio del messaggio');
            setStreamingContent('');
          }
        );
      }
      
      setSelectedFile(null);
      await loadConversations();
    } catch (e) {
      setMessages(prev => prev.filter(m => m.id !== tempUserMsg.id));
      if (e.message?.includes('temporaneamente')) {
        toast.error(e.message);
      } else {
        toast.error('Errore nell\'invio del messaggio');
      }
    } finally {
      setIsLoading(false);
      setStreamingContent('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDeleteConv = async (id, e) => {
    e?.stopPropagation();
    try {
      await api.deleteConversation(id);
      await loadConversations();
      if (conversationId === id) navigate('/chat');
      toast.success('Conversazione eliminata');
    } catch (e) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleRenameConv = async (id) => {
    if (!editTitleValue.trim()) return;
    try {
      await api.updateConversation(id, { title: editTitleValue.trim() });
      await loadConversations();
      setEditingTitle(null);
    } catch (e) {
      toast.error('Errore nella rinomina');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File troppo grande (max 10MB)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleSaveAsArtifact = (content, detectedLang = '') => {
    // Auto-detect title from first line
    let title = 'artifact';
    const firstLine = content.split('\n')[0].trim();
    if (firstLine.length > 0 && firstLine.length < 60) {
      title = firstLine.replace(/^[#/*\-\s]+/, '').trim() || 'artifact';
    }
    setSaveArtifactData({
      content,
      title: title.substring(0, 50),
      type: detectedLang ? 'code' : 'document',
      language: detectedLang || ''
    });
    if (projects.length > 0) {
      setSelectedProjectId(projects[0].id);
    }
    setShowSaveArtifact(true);
  };

  const handleConfirmSaveArtifact = async () => {
    if (!selectedProjectId) {
      toast.error('Seleziona un progetto');
      return;
    }
    try {
      await api.createArtifact(selectedProjectId, saveArtifactData);
      toast.success('Salvato come artifact!');
      setShowSaveArtifact(false);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const filteredConversations = conversations.filter(c =>
    !searchQuery || c.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const groupConversations = (convs) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const week = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    
    const groups = { today: [], week: [], older: [] };
    convs.forEach(c => {
      const d = new Date(c.updated_at || c.created_at);
      if (d >= today) groups.today.push(c);
      else if (d >= week) groups.week.push(c);
      else groups.older.push(c);
    });
    return groups;
  };

  const grouped = groupConversations(filteredConversations);

  const chatModels = models.filter(m => m.type === 'chat');

  return (
    <TooltipProvider>
      <div className="h-screen flex bg-[#0B0B0C] overflow-hidden relative" data-testid="chat-page">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}
        
        {/* Sidebar */}
        <div 
          className={`sidebar-transition flex-shrink-0 bg-[#101013] border-r border-white/[0.08] flex flex-col z-40
            fixed lg:relative h-full
            ${sidebarOpen ? 'w-[280px] left-0' : 'w-0 -left-[280px] lg:left-0 overflow-hidden'}`}
        >
          {/* Sidebar header */}
          <div className="p-4 flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_toyou-ai-white-label/artifacts/jx0csixl_TOYOU_logo-short-bianco.png" 
              alt="TY" 
              className="h-8 w-8 rounded"
            />
            <span className="text-white font-bold text-lg tracking-tight">
              <span className="font-medium">TO</span><span className="font-extrabold">YOU</span>
            </span>
          </div>

          {/* New chat button */}
          <div className="px-3 pb-2">
            <Button 
              onClick={handleNewChat}
              className="w-full bg-white text-black font-semibold hover:bg-white/90 h-10"
              data-testid="new-chat-button"
            >
              <Plus size={16} className="mr-2" /> Nuova chat
            </Button>
          </div>

          {/* Search */}
          <div className="px-3 pb-3">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <Input
                placeholder="Cerca..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 bg-white/[0.04] border-white/[0.06] text-white text-sm placeholder:text-white/30"
                data-testid="search-conversations-input"
              />
            </div>
          </div>

          <Separator className="bg-white/[0.06]" />

          {/* Conversation list */}
          <ScrollArea className="flex-1 px-2 py-2">
            {filteredConversations.length === 0 ? (
              <div className="text-center py-8 text-white/30 text-sm">
                Nessuna conversazione
              </div>
            ) : (
              <>
                {grouped.today.length > 0 && (
                  <ConvGroup label="Oggi" convs={grouped.today} currentId={conversationId} 
                    navigate={navigate} onDelete={handleDeleteConv}
                    editingTitle={editingTitle} setEditingTitle={setEditingTitle}
                    editTitleValue={editTitleValue} setEditTitleValue={setEditTitleValue}
                    onRename={handleRenameConv} />
                )}
                {grouped.week.length > 0 && (
                  <ConvGroup label="Ultimi 7 giorni" convs={grouped.week} currentId={conversationId}
                    navigate={navigate} onDelete={handleDeleteConv}
                    editingTitle={editingTitle} setEditingTitle={setEditingTitle}
                    editTitleValue={editTitleValue} setEditTitleValue={setEditTitleValue}
                    onRename={handleRenameConv} />
                )}
                {grouped.older.length > 0 && (
                  <ConvGroup label="Precedenti" convs={grouped.older} currentId={conversationId}
                    navigate={navigate} onDelete={handleDeleteConv}
                    editingTitle={editingTitle} setEditingTitle={setEditingTitle}
                    editTitleValue={editTitleValue} setEditTitleValue={setEditTitleValue}
                    onRename={handleRenameConv} />
                )}
              </>
            )}
          </ScrollArea>

          {/* User menu at bottom */}
          <div className="p-3 border-t border-white/[0.06] space-y-1">
            <button 
              onClick={() => navigate('/projects')}
              className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors text-left text-sm text-white/60 hover:text-white"
              data-testid="projects-nav-button"
            >
              <FolderOpen size={16} />
              <span>Progetti</span>
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors text-left">
                  <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center text-white text-sm font-semibold">
                    {user?.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate">{user?.name}</div>
                    <div className="text-xs text-white/40 truncate">{user?.email}</div>
                  </div>
                  <ChevronDown size={14} className="text-white/30" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56 bg-[#141418] border-white/[0.08]">
                <DropdownMenuItem onClick={() => navigate('/settings')} className="text-white/80 focus:bg-white/[0.06] focus:text-white cursor-pointer">
                  <Settings size={14} className="mr-2" /> Impostazioni
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} className="text-white/80 focus:bg-white/[0.06] focus:text-white cursor-pointer">
                    <Shield size={14} className="mr-2" /> Pannello Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={logout} className="text-red-400 focus:bg-red-400/10 focus:text-red-400 cursor-pointer">
                  <LogOut size={14} className="mr-2" /> Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Main chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top bar */}
          <div className="h-14 flex items-center px-4 border-b border-white/[0.06] bg-[#0B0B0C] gap-3">
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="text-white/50 hover:text-white hover:bg-white/[0.06] h-8 w-8"
                  data-testid="toggle-sidebar-button"
                >
                  {sidebarOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">{sidebarOpen ? 'Chiudi sidebar' : 'Apri sidebar'}</TooltipContent>
            </Tooltip>

            <div className="flex-1 flex items-center justify-center gap-4">
              {/* IAIA name */}
              <span className="text-sm text-white/40 tracking-tight hidden sm:inline" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
              </span>
              <div className="h-4 w-px bg-white/10 hidden sm:block" />
              {/* Model selector */}
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger 
                  className="w-[200px] h-8 bg-white/[0.04] border-white/[0.08] text-white text-sm"
                  data-testid="chat-model-selector"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#141418] border-white/[0.08]">
                  {chatModels.map(m => (
                    <SelectItem key={m.key} value={m.key} className="text-white focus:bg-white/[0.06] focus:text-white">
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="w-8" />
          </div>

          {/* Messages area */}
          <ScrollArea className="flex-1">
            <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6">
              {messages.length === 0 && !conversationId ? (
                <EmptyState onNewChat={handleNewChat} onPrompt={(text) => {
                  setInputText(text);
                  textareaRef.current?.focus();
                }} />
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-white/30 text-sm">
                  <span className="text-3xl mb-3 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    <span className="font-medium text-white/20">IA</span><span className="font-extrabold text-white/20">IA</span>
                  </span>
                  <span>Sono pronta, chiedi pure...</span>
                </div>
              ) : (
                messages.map((msg) => (
                  <MessageBubble key={msg.id} message={msg} onSaveArtifact={handleSaveAsArtifact} />
                ))
              )}
              
              {isLoading && (
                <div className="flex gap-3 mb-6">
                  <div className="h-8 w-8 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] text-white/70 tracking-tight leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
                    </span>
                  </div>
                  <div className="bg-[#141418] border border-white/[0.06] rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-white/40">IAIA sta scrivendo</span>
                      <div className="flex gap-1">
                        <div className="typing-dot h-1.5 w-1.5 bg-white/40 rounded-full" />
                        <div className="typing-dot h-1.5 w-1.5 bg-white/40 rounded-full" />
                        <div className="typing-dot h-1.5 w-1.5 bg-white/40 rounded-full" />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </ScrollArea>

          {/* Composer */}
          <div className="border-t border-white/[0.06] bg-[#0B0B0C]/80 backdrop-blur-sm px-3 sm:px-4 py-3">
            <div className="max-w-3xl mx-auto">
              {/* Selected file indicator */}
              {selectedFile && (
                <div className="mb-2 flex items-center gap-2 bg-white/[0.04] rounded-lg px-3 py-2">
                  <Paperclip size={14} className="text-white/40" />
                  <span className="text-sm text-white/60 truncate flex-1">{selectedFile.name}</span>
                  <button onClick={() => setSelectedFile(null)} className="text-white/30 hover:text-white/60">
                    <X size={14} />
                  </button>
                </div>
              )}
              
              <div className="flex items-end gap-2">
                <div className="flex-1 relative">
                  <Textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={imageGenMode ? 'Descrivi l\'immagine da generare...' : 'Chiedi qualcosa a IAIA...'}
                    className="min-h-[44px] max-h-[200px] bg-white/[0.04] border-white/[0.08] text-white placeholder:text-white/30 resize-none py-3 pr-24 text-sm focus:border-white/16 focus:ring-white/10"
                    data-testid="chat-composer-textarea"
                    rows={1}
                  />
                  
                  <div className="absolute right-2 bottom-2 flex items-center gap-1">
                    <input
                      ref={fileInputRef}
                      type="file"
                      onChange={handleFileSelect}
                      className="hidden"
                      accept="image/*,.pdf,.txt,.csv,.json,.md"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          className="h-7 w-7 text-white/30 hover:text-white/60 hover:bg-white/[0.06]"
                          data-testid="attach-file-button"
                        >
                          <Paperclip size={15} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>Allega file</TooltipContent>
                    </Tooltip>
                    
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setImageGenMode(!imageGenMode)}
                          className={`h-7 w-7 hover:bg-white/[0.06] ${imageGenMode ? 'text-white bg-white/10' : 'text-white/30 hover:text-white/60'}`}
                          data-testid="image-gen-toggle"
                        >
                          <ImageIcon size={15} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>{imageGenMode ? 'Disattiva generazione immagini' : 'Attiva generazione immagini'}</TooltipContent>
                    </Tooltip>
                  </div>
                </div>
                
                <Button
                  onClick={handleSend}
                  disabled={isLoading || (!inputText.trim() && !selectedFile)}
                  className="h-[44px] w-[44px] bg-white text-black hover:bg-white/90 disabled:opacity-30 flex-shrink-0"
                  data-testid="send-message-button"
                >
                  {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                </Button>
              </div>
              
              {imageGenMode && (
                <div className="mt-2 flex items-center gap-2 text-xs text-white/40">
                  <ImageIcon size={12} />
                  <span>Modalità generazione immagini attiva</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Save as Artifact Dialog */}
      <Dialog open={showSaveArtifact} onOpenChange={setShowSaveArtifact}>
        <DialogContent className="bg-[#141418] border-white/[0.08] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Bookmark size={16} /> Salva come Artifact
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-3">
            <div className="space-y-2">
              <Label className="text-white/60">Progetto</Label>
              {projects.length === 0 ? (
                <p className="text-white/40 text-sm">Nessun progetto. <button onClick={() => navigate('/projects')} className="underline text-white/60">Creane uno</button></p>
              ) : (
                <Select value={selectedProjectId} onValueChange={setSelectedProjectId}>
                  <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white">
                    <SelectValue placeholder="Seleziona progetto" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141418] border-white/[0.08]">
                    {projects.map(p => (
                      <SelectItem key={p.id} value={p.id} className="text-white focus:bg-white/[0.06] focus:text-white">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-white/60">Titolo</Label>
              <Input value={saveArtifactData.title} onChange={(e) => setSaveArtifactData({...saveArtifactData, title: e.target.value})}
                className="bg-white/[0.04] border-white/[0.08] text-white" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-white/60">Tipo</Label>
                <Select value={saveArtifactData.type} onValueChange={(v) => setSaveArtifactData({...saveArtifactData, type: v})}>
                  <SelectTrigger className="bg-white/[0.04] border-white/[0.08] text-white text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#141418] border-white/[0.08]">
                    <SelectItem value="code" className="text-white focus:bg-white/[0.06]">Codice</SelectItem>
                    <SelectItem value="document" className="text-white focus:bg-white/[0.06]">Documento</SelectItem>
                    <SelectItem value="text" className="text-white focus:bg-white/[0.06]">Testo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-white/60">Linguaggio</Label>
                <Input value={saveArtifactData.language} onChange={(e) => setSaveArtifactData({...saveArtifactData, language: e.target.value})}
                  placeholder="es. python, javascript" className="bg-white/[0.04] border-white/[0.08] text-white text-sm" />
              </div>
            </div>
            <div className="bg-white/[0.03] rounded-lg p-3 max-h-32 overflow-auto">
              <pre className="text-xs text-white/50 whitespace-pre-wrap font-mono">{saveArtifactData.content?.substring(0, 300)}{saveArtifactData.content?.length > 300 ? '...' : ''}</pre>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveArtifact(false)} className="border-white/10 text-white/60 hover:bg-white/[0.04]">Annulla</Button>
            <Button onClick={handleConfirmSaveArtifact} disabled={!selectedProjectId || !saveArtifactData.title} className="bg-white text-black hover:bg-white/90">
              <Bookmark size={14} className="mr-2" /> Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}

// ---- Sub Components ----

function ConvGroup({ label, convs, currentId, navigate, onDelete, editingTitle, setEditingTitle, editTitleValue, setEditTitleValue, onRename }) {
  return (
    <div className="mb-4">
      <div className="px-3 py-1 text-xs text-white/30 font-medium uppercase tracking-wider">{label}</div>
      {convs.map(conv => (
        <div
          key={conv.id}
          className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
            currentId === conv.id ? 'bg-white/[0.08]' : 'hover:bg-white/[0.04]'
          }`}
          onClick={() => navigate(`/chat/${conv.id}`)}
          data-testid="conversation-list-item"
        >
          <MessageSquare size={14} className="text-white/30 flex-shrink-0" />
          {editingTitle === conv.id ? (
            <Input
              value={editTitleValue}
              onChange={(e) => setEditTitleValue(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') onRename(conv.id); if (e.key === 'Escape') setEditingTitle(null); }}
              onBlur={() => onRename(conv.id)}
              className="h-6 text-xs bg-transparent border-white/10 text-white px-1"
              autoFocus
              onClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="flex-1 text-sm text-white/70 truncate">{conv.title}</span>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <button className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-white/60 transition-opacity p-1">
                <MoreHorizontal size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#141418] border-white/[0.08]" align="end">
              <DropdownMenuItem 
                className="text-white/70 focus:bg-white/[0.06] focus:text-white cursor-pointer text-xs"
                onClick={(e) => { e.stopPropagation(); setEditingTitle(conv.id); setEditTitleValue(conv.title); }}
              >
                <Pencil size={12} className="mr-2" /> Rinomina
              </DropdownMenuItem>
              <DropdownMenuItem 
                className="text-red-400 focus:bg-red-400/10 focus:text-red-400 cursor-pointer text-xs"
                onClick={(e) => onDelete(conv.id, e)}
              >
                <Trash2 size={12} className="mr-2" /> Elimina
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ))}
    </div>
  );
}

function EmptyState({ onNewChat, onPrompt }) {
  const suggestions = [
    { icon: '\u2328\uFE0F', title: 'Scrivi codice', prompt: 'Scrivi una funzione Python che ordini una lista di dizionari per chiave' },
    { icon: '\u{1F4DD}', title: 'Redigi un\'email', prompt: 'Scrivi un\'email professionale per ringraziare un cliente' },
    { icon: '\u{1F4CA}', title: 'Analizza dati', prompt: 'Come posso analizzare i dati di vendita mensili con Python e pandas?' },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="mb-6">
        <span className="text-5xl text-white/30 tracking-tight" style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
        </span>
      </div>
      <h2 className="text-xl font-semibold text-white/80 mb-2">Ciao, sono IAIA. Come posso aiutarti?</h2>
      <p className="text-sm text-white/40 mb-8">Scegli un suggerimento o inizia a scrivere</p>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full max-w-2xl">
        {suggestions.map((s, i) => (
          <button
            key={i}
            onClick={() => onPrompt(s.prompt)}
            className="text-left p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] hover:border-white/[0.1] transition-colors"
            data-testid={`suggestion-${i}`}
          >
            <div className="text-2xl mb-2">{s.icon}</div>
            <div className="text-sm text-white/70 font-medium">{s.title}</div>
            <div className="text-xs text-white/30 mt-1 line-clamp-2">{s.prompt}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({ message, onSaveArtifact }) {
  const isUser = message.role === 'user';
  const hasImage = message.image_base64 || message.content?.includes('data:image');
  const [showActions, setShowActions] = useState(false);

  const handleCopyMessage = () => {
    navigator.clipboard.writeText(message.content || '');
    toast.success('Copiato!');
  };

  // Extract code blocks from content for individual save
  const extractCodeBlocks = (content) => {
    const regex = /```(\w*)\n([\s\S]*?)```/g;
    const blocks = [];
    let match;
    while ((match = regex.exec(content)) !== null) {
      blocks.push({ language: match[1] || 'text', code: match[2].trim() });
    }
    return blocks;
  };

  return (
    <div 
      className={`flex gap-3 mb-6 ${isUser ? 'flex-row-reverse' : ''} group`}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isUser ? 'bg-white text-black font-semibold text-sm' : 'bg-white/10'
      }`}>
        {isUser ? 'U' : (
          <span className="text-[10px] text-white/70 tracking-tight leading-none" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <span className="font-medium">IA</span><span className="font-extrabold">IA</span>
          </span>
        )}
      </div>
      <div className={`max-w-[80%] ${isUser ? 'text-right' : ''}`}>
        <div className={`rounded-2xl px-4 py-3 ${
          isUser 
            ? 'bg-white/[0.08] rounded-tr-sm text-white' 
            : 'bg-[#141418] border border-white/[0.06] rounded-tl-sm text-white/90'
        }`}>
          {hasImage && message.image_base64 ? (
            <div>
              <img 
                src={`data:image/png;base64,${message.image_base64}`}
                alt="Generated" 
                className="rounded-lg max-w-full mb-2"
              />
            </div>
          ) : hasImage && message.content?.includes('data:image') ? (
            <div>
              {(() => {
                const match = message.content.match(/!\[.*?\]\((data:image\/[^)]+)\)/);
                if (match) {
                  return <img src={match[1]} alt="Generated" className="rounded-lg max-w-full mb-2" />;
                }
                return <MarkdownRenderer content={message.content} />;
              })()}
            </div>
          ) : (
            <div className={`chat-markdown text-sm leading-relaxed`}>
              <MarkdownRenderer content={message.content || ''} />
            </div>
          )}
        </div>
        
        {/* Action buttons for assistant messages */}
        {!isUser && message.content && !message._streaming && (
          <div className={`flex items-center gap-1 mt-1.5 transition-opacity duration-150 ${showActions ? 'opacity-100' : 'opacity-0'}`}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button onClick={handleCopyMessage} className="p-1.5 rounded-md text-white/25 hover:text-white/60 hover:bg-white/[0.04] transition-colors">
                  <Copy size={13} />
                </button>
              </TooltipTrigger>
              <TooltipContent>Copia messaggio</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button 
                  onClick={() => onSaveArtifact && onSaveArtifact(message.content, '')} 
                  className="p-1.5 rounded-md text-white/25 hover:text-white/60 hover:bg-white/[0.04] transition-colors"
                  data-testid="save-artifact-button"
                >
                  <Bookmark size={13} />
                </button>
              </TooltipTrigger>
              <TooltipContent>Salva come artifact</TooltipContent>
            </Tooltip>
            {/* Save individual code blocks */}
            {extractCodeBlocks(message.content).length > 0 && extractCodeBlocks(message.content).map((block, i) => (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <button 
                    onClick={() => onSaveArtifact && onSaveArtifact(block.code, block.language)} 
                    className="flex items-center gap-1 px-2 py-1 rounded-md text-white/25 hover:text-white/60 hover:bg-white/[0.04] transition-colors text-[11px]"
                  >
                    <Bookmark size={11} /> {block.language || 'code'}
                  </button>
                </TooltipTrigger>
                <TooltipContent>Salva blocco {block.language} come artifact</TooltipContent>
              </Tooltip>
            ))}
          </div>
        )}
        
        {message.attachments?.length > 0 && message.attachments[0]?.name && (
          <div className="mt-1 flex items-center gap-1 text-xs text-white/30">
            <Paperclip size={10} />
            <span>{message.attachments[0].name}</span>
          </div>
        )}
      </div>
    </div>
  );
}
